import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PublicSourceAnalyticsSchedule, SourceScheduleInput } from "../../../shared/types";
import { api } from "../lib/api";

const key = (brandId: string) => ["source-schedules", brandId] as const;
const path = (brandId: string) => `/api/brands/${encodeURIComponent(brandId)}/analytics/schedules`;

export function useSourceSchedules(brandId: string) {
  return useQuery({ queryKey: key(brandId), enabled: !!brandId,
    queryFn: () => api.get<{ data: PublicSourceAnalyticsSchedule[] }>(path(brandId)), refetchInterval: 30_000 });
}

export function useSaveSourceSchedule(brandId: string, source: PublicSourceAnalyticsSchedule) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (change: { schedule: SourceScheduleInput; expectedRevision?: number } | { enabled: boolean }) => {
      if (source.schedule && "schedule" in change && change.expectedRevision === undefined) throw new Error("A schedule was created while you were editing. Reload and review it before saving.");
      if (source.schedule) return api.patch(`${path(brandId)}/${encodeURIComponent(source.schedule.id)}`,
        "schedule" in change ? { expected_revision: change.expectedRevision ?? source.schedule.revision, schedule: change.schedule }
          : { expected_revision: source.schedule.revision, enabled: change.enabled });
      if (!("schedule" in change)) throw new Error("Save a schedule before enabling it.");
      return api.post(`${path(brandId)}/${encodeURIComponent(source.assignment_id)}`,
        { capability: source.capability, schedule: change.schedule });
    },
    // Even a failed response can follow a committed write. Reload both projections.
    onSettled: async () => { await Promise.all([
      client.invalidateQueries({ queryKey: key(brandId) }),
      client.invalidateQueries({ queryKey: ["configured-actions", brandId] }),
      client.invalidateQueries({ queryKey: ["analytics-refresh", brandId] }),
    ]); },
  });
}

export function useSaveSourceAction(brandId:string,source:PublicSourceAnalyticsSchedule){
  const client=useQueryClient();
  return useMutation({mutationFn:()=>api.post(`/api/v1/brands/${encodeURIComponent(brandId)}/configured-actions/source-analytics`,
    {assignment_id:source.assignment_id,capability:source.capability}),
    onSettled:async()=>{await Promise.all([client.invalidateQueries({queryKey:key(brandId)}),client.invalidateQueries({queryKey:["configured-actions",brandId]})]);}});
}

/**
 * Finish a source's setup in one owner action: save its calendar through the existing
 * schedule service (which always creates it paused and enrolls the source's reports), then
 * turn that schedule on with the existing revision-checked enable. Nothing here enables a
 * schedule the owner did not ask for: this only runs from an explicit "Turn on" click.
 */
export function useTurnOnDailyRefresh(brandId: string, source: PublicSourceAnalyticsSchedule) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (schedule: SourceScheduleInput) => {
      if (source.schedule) throw new Error("This source already has a schedule. Reload and review it before changing it.");
      const created = await api.post<{ data: PublicSourceAnalyticsSchedule }>(`${path(brandId)}/${encodeURIComponent(source.assignment_id)}`,
        { capability: source.capability, schedule });
      const saved = created.data.schedule;
      if (!saved?.enrolled) throw new Error("The schedule was saved, but source setup is incomplete.");
      if (saved.enabled) return created.data;
      const enabled = await api.patch<{ data: PublicSourceAnalyticsSchedule }>(`${path(brandId)}/${encodeURIComponent(saved.id)}`,
        { expected_revision: saved.revision, enabled: true });
      return enabled.data;
    },
    // A failure after the create still leaves a saved (paused) schedule. Reload so the
    // panel shows that schedule and its On/Off switch instead of offering setup again.
    onSettled: async () => { await Promise.all([
      client.invalidateQueries({ queryKey: key(brandId) }),
      client.invalidateQueries({ queryKey: ["configured-actions", brandId] }),
      client.invalidateQueries({ queryKey: ["analytics-refresh", brandId] }),
      client.invalidateQueries({ queryKey: ["brand-analytics-properties", brandId] }),
    ]); },
  });
}
